import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone } from 'lucide-react';
import { FEATURE_FLAGS } from '../config/featureFlags';
import { CONTACT_INFO } from '../config/contact';
import { useSEOContext } from '../contexts/SEOContext';
import { useLocationContext } from '../contexts/LocationContext';
import { useDisclaimer } from '../contexts/DisclaimerContext';
import { useTranslation } from '../hooks/useTranslation';

const Footer: React.FC = () => {
  const { seoData } = useSEOContext();
  const { relatedCities } = useLocationContext();
  const { hasAcceptedDisclaimer } = useDisclaimer();
  const { t } = useTranslation();

  // Non mostrare il footer se il disclaimer non è stato accettato
  if (!hasAcceptedDisclaimer) {
    return null;
  }

  return (
    <footer className="bg-gray-900 text-white pt-8 pb-6">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div>
            <h3 className="text-xl font-semibold mb-4 text-pink-400">EscortFun.net</h3>
            <p className="text-gray-400 text-sm mb-4">
              {t('footer.siteDescription')}
            </p>
            <div className="space-y-2">
              <div className="flex items-center">
                <Mail className="h-4 w-4 text-pink-400 mr-2" />
                <a href={`mailto:${CONTACT_INFO.email}`} className="text-gray-400 hover:text-pink-400 transition-colors text-sm">
                  {CONTACT_INFO.email}
                </a>
              </div>
              <div className="flex items-center">
                <Phone className="h-4 w-4 text-pink-400 mr-2" />
                <a href={`tel:${CONTACT_INFO.phoneHref}`} className="text-gray-400 hover:text-pink-400 transition-colors text-sm">
                  {CONTACT_INFO.phone}
                </a>
              </div>
            </div>
          </div>

          {seoData && relatedCities.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold mb-4 text-pink-400">{t('footer.escortsInCity', { cityName: seoData.cityName })}</h3>
              <ul className="space-y-2">
                {relatedCities.map((city, index) => (
                  <li key={index}>
                    <Link to={`/location/${city}`} className="text-gray-400 hover:text-pink-400 transition-colors duration-200 text-sm">
                      {city}
                    </Link>
                  </li>))
                }
              </ul>
            </div>)
          }
          
          <div>
            <h3 className="text-lg font-semibold mb-4 text-pink-400">{t('footer.quickLinksTitle')}</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-gray-400 hover:text-pink-400 transition-colors duration-200 text-sm">
                  {t('footer.homeLink')}
                </Link>
              </li>
              <li>
                <Link to="/escorts" className="text-gray-400 hover:text-pink-400 transition-colors duration-200 text-sm">
                  {t('common.tutte_le_escort')}
                </Link>
              </li>
              <li>
                <Link to="/contatti" className="text-gray-400 hover:text-pink-400 transition-colors duration-200 text-sm">
                  {t('footer.contactLink')}
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-gray-400 hover:text-pink-400 transition-colors duration-200 text-sm">
                  {t('common.blogLink')}
                </Link>
              </li>
            </ul>
          </div>

          {FEATURE_FLAGS.SHOW_LOGIN_REGISTER && (
            <div>
              <h3 className="text-lg font-semibold mb-4 text-pink-400">{t('footer.forEscortsTitle')}</h3>
              <ul className="space-y-2">
                  <>
                    <li>
                      <Link to="/auth" className="text-gray-400 hover:text-pink-400 transition-colors duration-200 text-sm">
                        {t('common.accedi_registrati')}
                      </Link>
                    </li>
                  </>
              </ul>
            </div>
          )}
          
          <div>
            <h3 className="text-lg font-semibold mb-4 text-pink-400">{t('footer.contactTitle')}</h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <Mail size={18} className="text-pink-400 mr-2 mt-0.5" />
                <a href={`mailto:${CONTACT_INFO.email}`} className="text-gray-400 hover:text-pink-400 transition-colors text-sm">
                  {CONTACT_INFO.email}
                </a>
              </li>
              <li className="flex items-start">
                <Phone size={18} className="text-pink-400 mr-2 mt-0.5" />
                <a href={`tel:${CONTACT_INFO.phoneHref}`} className="text-gray-400 hover:text-pink-400 transition-colors text-sm">
                  {CONTACT_INFO.phone}
                </a>
              </li>
            </ul>
          </div>
        </div>
        
        <hr className="border-gray-800 my-8" />
        
        <div className="flex flex-col md:flex-row justify-between items-center">
          <p className="text-gray-500 text-sm" dangerouslySetInnerHTML={{ 
            __html: t('footer.copyrightText', { year: new Date().getFullYear() }) 
          }} />
          {/* Remove dead policy links for now */}
        </div>
      </div>
    </footer>
  );
};

export default Footer;